// frontend/src/pages/HomePage.jsx
import { useEffect, useState } from 'react';
import config from '../config';

import { Link } from 'react-router-dom';
import { Sun, Wrench, Shield, Truck, Loader2, ShoppingCart, Zap } from 'lucide-react';
import { PackageCard } from '../components/PackageCard';
import { Banner } from '../components/Banner';

export const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // دریافت محصولات ویژه
        const featuredResponse = await fetch(config.getApiUrl('/products/products/featured/'));
        if (featuredResponse.ok) {
          const featuredData = await featuredResponse.json();
          const products = featuredData.results || featuredData || [];
          setFeaturedProducts(products);
        }
        
        // دریافت پکیج‌های پیشنهادی
        const packagesResponse = await fetch(config.getApiUrl('/products/packages/'));
        if (packagesResponse.ok) {
          const packagesData = await packagesResponse.json();
          const packagesList = packagesData.results || packagesData || [];
          setPackages(packagesList);
        }
      } catch (err) {
        console.error('Error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const features = [
    { icon: Sun, title: 'بهترین تجهیزات', description: 'محصولات با کیفیت از برندهای معتبر جهانی' },
    { icon: Wrench, title: 'نصب حرفه‌ای', description: 'نصب توسط تکنسین‌های مجرب و دارای گواهینامه' },
    { icon: Shield, title: 'گارانتی معتبر', description: 'گارانتی اصالت و سلامت فیزیکی کالا' },
    { icon: Truck, title: 'ارسال سریع', description: 'ارسال به سراسر کشور در کمترین زمان' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center text-red-600">
          <p className="text-xl">خطا در بارگذاری صفحه</p>
          <p className="text-sm mt-2">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 bg-blue-500 text-white px-4 py-2 rounded"
          >
            تلاش مجدد
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* فقط بنر داینامیک از CMS - حذف شد */}
      <Banner position="home_hero" />

      {/* Features - بدون تغییر */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature) => (
              <div key={feature.title} className="text-center p-6 rounded-xl hover:bg-gray-50 transition-colors">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* پکیج‌های پیشنهادی */}
      {packages.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
                <Zap className="w-6 h-6 text-yellow-500" />
                پکیج‌های پیشنهادی
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {packages.map((pkg) => (
                <PackageCard key={pkg.id} package={pkg} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Products */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">محصولات ویژه</h2>
            <Link to="/products" className="text-blue-600 hover:text-blue-700 font-medium">
              مشاهده همه
            </Link>
          </div>

          {featuredProducts.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>هیچ محصول ویژه‌ای یافت نشد.</p>
              <p className="text-sm mt-2">لطفاً در پنل ادمین محصولات ویژه را تعریف کنید.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.map((product) => (
                <div key={product.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition">
                  <Link to={`/products/${product.slug}`}>
                    <img
                      src={product.main_image || 'https://via.placeholder.com/300x200?text=No+Image'}
                      alt={product.name}
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/300x200?text=No+Image';
                      }}
                    />
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-800 mb-2">{product.name}</h3>
                      <div className="flex items-center justify-between">
                        <p className="text-blue-600 font-bold">{product.final_price?.toLocaleString()} تومان</p>
                        {product.brand && (
                          <span className="text-xs text-gray-400">{product.brand}</span>
                        )}
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            آماده صرفه‌جویی در قبض برق هستید؟
          </h2>
          <p className="text-xl mb-8 text-yellow-100">
            همین حالا مشاوره رایگان دریافت کنید و بهترین سیستم را انتخاب کنید
          </p>
          <Link
            to="/installation/request"
            className="inline-block bg-white text-yellow-600 px-8 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors"
          >
            شروع کنید
          </Link>
        </div>
      </section>
    </div>
  );
};